/**
 * Home Page
 * Landing page — Paiseh! design refresh (Paiseh A.dc)
 */

import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo/SEO'
import { StructuredData } from '@/components/seo/StructuredData'
import { PaisehLogo } from '@/components/layout/Header'

/** Cross-hatched "product shot" placeholder swatch from the design */
const SWATCH_AVAIL =
  'repeating-linear-gradient(135deg,#F4E7D6,#F4E7D6 9px,#EFDFC9 9px,#EFDFC9 18px)'

export const Home = () => {
  return (
    <div className="min-h-screen bg-cream text-ink font-jakarta">
      <SEO />
      <StructuredData type="organization" />
      <StructuredData type="website" />
      <StructuredData type="softwareApplication" />
      <StructuredData
        type="faq"
        questions={[
          {
            question: 'What is Paiseh! and how does it work?',
            answer:
              "Paiseh! is a free online wishlist maker for Singapore. You can create wishlists for any occasion, add items you want, and share the link with friends and family. When someone decides to buy a gift, they can reserve it so others know it's taken - but you won't see who reserved what, keeping the surprise!",
          },
          {
            question: 'Is Paiseh! free to use?',
            answer:
              'Yes, Paiseh! is completely free! You can create unlimited wishlists, add as many items as you want, and share with anyone. No hidden fees or premium tiers.',
          },
          {
            question: 'Do I need to create an account to use Paiseh!?',
            answer:
              'No account is required to start using Paiseh!. You can create wishlists immediately. However, creating a free account lets you sync your wishlists across devices and access them from anywhere.',
          },
          {
            question: 'How do I share my wishlist with others?',
            answer:
              'Each wishlist has a unique shareable link. Simply copy the link and send it via WhatsApp, email, or any messaging app. Anyone with the link can view your wishlist and reserve items.',
          },
          {
            question: 'Can people see what gifts others have reserved?',
            answer:
              'Gift-givers can see which items are already reserved to avoid duplicate gifts. However, you (the wishlist owner) cannot see who reserved what - this keeps the gift a surprise!',
          },
        ]}
      />

      {/* ================= HERO ================= */}
      <section className="max-w-[1180px] mx-auto grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 px-5 sm:px-10 pt-14 pb-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-paiseh-peach text-paiseh-orangeDark text-[13px] font-bold px-3.5 py-[7px] rounded-full mb-[22px]">
            🇸🇬 Made in Singapore lah
          </div>
          <h1 className="font-display font-extrabold text-[44px] sm:text-[64px] leading-[1.01] tracking-[-0.02em] mb-[22px]">
            Don&rsquo;t paiseh &mdash;<br />
            just share your <span className="text-paiseh-orange">wishlist.</span>
          </h1>
          <p className="text-[19px] leading-[1.55] text-paiseh-muted mb-[34px] max-w-[440px]">
            Tell everyone what you actually want. Friends quietly reserve gifts so there are no
            duplicates &mdash; and no spoiled surprises.
          </p>
          <div className="flex flex-wrap gap-3.5 items-center">
            <Link
              to="/wishlists"
              className="bg-paiseh-orange text-white text-[17px] font-bold px-[30px] py-4 rounded-full shadow-[0_8px_20px_rgba(240,98,62,.32)] hover:opacity-95 transition-opacity"
            >
              Start my wishlist →
            </Link>
            <a
              href="#how-it-works"
              className="text-[17px] font-bold text-ink px-[22px] py-4 hover:text-paiseh-orange transition-colors"
            >
              See how it works
            </a>
          </div>
          <div className="flex items-center gap-2.5 mt-[30px] text-sm text-[#8A7E72] font-semibold">
            <div className="flex">
              <div className="w-7 h-7 rounded-full bg-paiseh-orange border-2 border-cream" />
              <div className="w-7 h-7 rounded-full bg-paiseh-gold border-2 border-cream -ml-2.5" />
              <div className="w-7 h-7 rounded-full bg-paiseh-olive border-2 border-cream -ml-2.5" />
            </div>
            Loved by thousands of kiasu gift-givers
          </div>
        </div>

        {/* Floating preview cards */}
        <div className="relative h-[440px] hidden lg:block">
          <div className="absolute top-[30px] left-5 w-[330px] bg-white rounded-[22px] p-5 shadow-[0_20px_50px_rgba(43,36,32,.12)] animate-floaty">
            <div
              className="h-[158px] rounded-[14px] flex items-end p-2.5"
              style={{ background: SWATCH_AVAIL }}
            >
              <span className="font-grotesk text-[11px] text-[#A8957C]">product shot</span>
            </div>
            <div className="flex justify-between items-center mt-3.5">
              <span className="font-bold text-base">Stanley Tumbler</span>
              <span className="font-bold text-paiseh-orange">$55</span>
            </div>
            <div className="mt-3 bg-paiseh-orange text-white text-center font-bold text-sm py-[11px] rounded-full">
              Reserve this gift
            </div>
          </div>
          <div className="absolute bottom-2.5 right-0 w-[255px] bg-ink text-cream rounded-[20px] px-5 py-[18px] shadow-[0_16px_40px_rgba(43,36,32,.22)] animate-floaty2">
            <div className="text-[13px] text-[#C9BBA8] font-semibold">Reserved by someone 🤫</div>
            <div className="font-bold text-[17px] mt-1.5">Polaroid Camera</div>
            <div className="text-[13px] text-[#9C8C7A] mt-1">Hannah won&rsquo;t know it&rsquo;s you</div>
          </div>
        </div>
      </section>

      {/* ================= FEATURE CARDS ================= */}
      <section className="max-w-[1180px] mx-auto px-5 sm:px-10 pt-2.5 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[22px]">
          <Link
            to="/wishlists"
            className="block bg-white rounded-[22px] p-[30px] border border-ink/[.06] hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 rounded-[14px] bg-paiseh-peach text-paiseh-orange flex items-center justify-center text-2xl">
              📝
            </div>
            <h3 className="font-display font-bold text-[23px] mt-[18px] mb-2">My Wishlists</h3>
            <p className="text-[15px] leading-[1.55] text-paiseh-muted">
              Add anything from any store, sort by occasion, and share one tidy link.
            </p>
          </Link>
          <Link
            to="/reservations"
            className="block bg-white rounded-[22px] p-[30px] border border-ink/[.06] hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 rounded-[14px] bg-paiseh-oliveTint text-paiseh-olive flex items-center justify-center text-2xl">
              🎁
            </div>
            <h3 className="font-display font-bold text-[23px] mt-[18px] mb-2">My Reservations</h3>
            <p className="text-[15px] leading-[1.55] text-paiseh-muted">
              Keep track of gifts you&rsquo;ve claimed so you never double-buy or forget.
            </p>
          </Link>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how-it-works" className="bg-ink text-cream">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-10 pt-16 pb-[70px]">
          <h2 className="font-display font-extrabold text-[32px] sm:text-4xl text-center mb-[42px]">
            How Paiseh! works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[26px]">
            {[
              {
                num: '01',
                color: 'text-paiseh-orange',
                title: 'Make your list',
                body: 'Paste links, set prices, pick the occasion.',
              },
              {
                num: '02',
                color: 'text-paiseh-gold',
                title: 'Share the link',
                body: 'Drop it in the family chat. No sign-up needed to view.',
              },
              {
                num: '03',
                color: 'text-paiseh-olive',
                title: 'They reserve, secretly',
                body: 'Gifts get marked taken — but you stay surprised.',
              },
            ].map((step) => (
              <div key={step.num}>
                <div className={`font-display font-extrabold text-base ${step.color}`}>{step.num}</div>
                <h4 className="text-xl font-bold my-2">{step.title}</h4>
                <p className="text-[14.5px] leading-[1.55] text-[#B6A896]">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="max-w-[1180px] mx-auto px-5 sm:px-10 py-10 mt-5 border-t border-ink/[.08] flex flex-col sm:flex-row items-center justify-between gap-4">
        <Link to="/">
          <PaisehLogo size="sm" />
        </Link>
        <div className="flex flex-wrap items-center justify-center gap-4 text-[13px] text-paiseh-faint font-semibold">
          <Link to="/privacy" className="hover:text-paiseh-orange transition-colors">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-paiseh-orange transition-colors">
            Terms
          </Link>
          <Link to="/cookies" className="hover:text-paiseh-orange transition-colors">
            Cookies
          </Link>
          <span>Made with ❤️ in Singapore · paiseh.sg</span>
        </div>
      </footer>
    </div>
  )
}
